import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { getActiveProducts } from "../firebase/productService";
import { getPricing } from "../firebase/pricingService";
import { SIZES, FRAME_TYPES } from "../firebase/constants";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');
`;

/* ── Animations ── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const lineGrow = keyframes`
  from { width: 0; }
  to   { width: 48px; }
`;

/* ── Page ── */
const Page = styled.div`
  min-height: 100vh;
  background: #080604;
  font-family: "Jost", sans-serif;
`;

/* ── Hero Header ── */
const Hero = styled.div`
  padding: 140px 5vw 60px;
  position: relative;
  border-bottom: 1px solid rgba(201, 169, 110, 0.1);

  @media (max-width: 768px) {
    padding: 110px 6vw 48px;
  }
`;

const HeroEyebrow = styled.p`
  font-family: "Jost", sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0 0 20px 0;
`;

const HeroTitle = styled.h1`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(3.5rem, 8vw, 7rem);
  color: #f5ede0;
  letter-spacing: -0.01em;
  line-height: 0.92;
  margin: 0 0 28px 0;

  em {
    font-style: italic;
    color: rgba(245, 237, 224, 0.45);
  }
`;

const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const GoldLine = styled.div`
  height: 1px;
  background: #c9a96e;
  animation: ${lineGrow} 1s ease 0.5s forwards;
  width: 0;
`;

const HeroCount = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.35);
`;

/* ── Toolbar ── */
const Toolbar = styled.div`
  padding: 28px 5vw;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 20px 6vw;
    gap: 12px;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 360px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(201, 169, 110, 0.5);
  font-size: 0.8rem;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(201, 169, 110, 0.15);
  color: #f5ede0;
  font-family: "Jost", sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  padding: 11px 16px 11px 38px;
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.3s,
    background 0.3s;

  &::placeholder {
    color: rgba(245, 237, 224, 0.2);
  }
  &:focus {
    border-color: rgba(201, 169, 110, 0.45);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button`
  font-family: "Jost", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 9px 18px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? "#c9a96e" : "rgba(255,255,255,0.08)")};
  background: ${(p) => (p.$active ? "rgba(201,169,110,0.12)" : "transparent")};
  color: ${(p) => (p.$active ? "#c9a96e" : "rgba(245,237,224,0.4)")};
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(201, 169, 110, 0.5);
    color: #c9a96e;
    background: rgba(201, 169, 110, 0.06);
  }
`;

const SortSelect = styled.select`
  margin-left: auto;
  font-family: "Jost", sans-serif;
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(245, 237, 224, 0.4);
  padding: 9px 14px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 0.25s,
    color 0.25s;

  option {
    background: #111;
    color: #f5ede0;
  }

  &:hover,
  &:focus {
    border-color: rgba(201, 169, 110, 0.4);
    color: #c9a96e;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const ResultCount = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.2);
  white-space: nowrap;
`;

/* ── Grid ── */
const GridWrapper = styled.div`
  padding: 32px 5vw 100px;

  @media (max-width: 768px) {
    padding: 24px 6vw 80px;
  }
`;

const Grid = styled.div`
  columns: 4;
  column-gap: 20px;
  @media (max-width: 1100px) {
    columns: 3;
  }
  @media (max-width: 700px) {
    columns: 2;
  }
  @media (max-width: 400px) {
    columns: 1;
  }
`;

/* ── Card ── */
const Card = styled.article`
  background: #080604;
  cursor: pointer;
  position: relative;
  animation: ${fadeUp} 0.6s ease forwards;
  animation-delay: ${(p) => Math.min(p.$index * 0.07, 0.5)}s;
  opacity: 0;
  break-inside: avoid;
  margin-bottom: 20px;

  &:hover .card-img img {
    transform: scale(1.07);
  }
  &:hover .card-overlay {
    opacity: 1;
  }
  &:hover .card-line {
    width: 24px;
  }
  &:hover .card-arrow {
    transform: translateX(4px);
    opacity: 1;
  }
`;

const CardImageWrap = styled.div`
  width: 100%;
  height: auto;
  overflow: hidden;
  background: #111;
  position: relative;
`;

const CardImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const NoImg = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0f0d0a 0%, #1a1612 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(201, 169, 110, 0.15);
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 40%,
    rgba(8, 6, 4, 0.7) 100%
  );
  opacity: 0;
  transition: opacity 0.4s ease;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 28px;
`;

const ViewBtn = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #f5ede0;
  border: 1px solid rgba(201, 169, 110, 0.55);
  padding: 9px 26px;
  background: rgba(8, 6, 4, 0.4);
  backdrop-filter: blur(8px);
`;

/* ── Card number badge ── */
const CardNumber = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  font-family: "Jost", sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.16em;
  color: rgba(201, 169, 110, 0.5);
  background: rgba(8, 6, 4, 0.65);
  backdrop-filter: blur(6px);
  padding: 4px 8px;
`;

/* ── Card Info ── */
const CardInfo = styled.div`
  padding: 14px 14px 16px;
  border-top: 1px solid rgba(201, 169, 110, 0.07);
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const CardName = styled.h3`
  font-family: "Cormorant Garamond", serif;
  font-weight: 400;
  font-size: 1rem;
  color: #f5ede0;
  letter-spacing: 0.02em;
  margin: 0;
  line-height: 1.2;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardArrow = styled.span`
  color: #c9a96e;
  font-size: 0.9rem;
  opacity: 0;
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
  flex-shrink: 0;
  margin-top: 2px;
`;

const CardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardCategory = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.58rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(201, 169, 110, 0.5);
`;

const CardPrice = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  color: rgba(245, 237, 224, 0.5);

  strong {
    color: #c9a96e;
    font-weight: 400;
  }
`;

const CardLine = styled.div`
  height: 1px;
  background: rgba(201, 169, 110, 0.3);
  width: 0;
  transition: width 0.4s ease;
  margin-top: 2px;
`;

/* ── Skeleton ── */
const SkeletonCard = styled.div`
  background: #080604;
`;

const SkeletonImg = styled.div`
  width: 100%;
  height: 220px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.02) 25%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.02) 75%
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.6s infinite;

  @media (max-width: 700px) {
    height: 180px;
  }
`;

const SkeletonInfo = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SkeletonLine = styled.div`
  height: ${(p) => p.$h || "12px"};
  width: ${(p) => p.$w || "100%"};
  background: rgba(255, 255, 255, 0.04);
  border-radius: 1px;
`;

/* ── Empty ── */
const EmptyWrap = styled.div`
  grid-column: 1 / -1;
  padding: 120px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const EmptyIcon = styled.div`
  font-size: 2rem;
  opacity: 0.12;
`;

const EmptyText = styled.p`
  font-family: "Jost", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.2);
  margin: 0;
`;

const ClearBtn = styled.button`
  font-family: "Jost", sans-serif;
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #c9a96e;
  background: none;
  border: 1px solid rgba(201, 169, 110, 0.3);
  padding: 8px 20px;
  cursor: pointer;
  transition: all 0.25s;
  margin-top: 4px;

  &:hover {
    background: rgba(201, 169, 110, 0.08);
    border-color: #c9a96e;
  }
`;

/* ── Helpers ── */
const getStartingPrice = (pricing) => {
  if (!pricing) return null;
  let min = Infinity;
  Object.values(pricing).forEach((sizes) => {
    Object.values(sizes).forEach((price) => {
      if (price > 0 && price < min) min = price;
    });
  });
  return min === Infinity ? null : min;
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
];

/* ── Component ── */
const Shop = ({ category }) => {
  const [products, setProducts] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [data, pr] = await Promise.all([
          getActiveProducts(),
          getPricing(),
        ]);
        setProducts(data);
        setPricing(pr);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = category
      ? products.filter((p) => p.category === category)
      : products;

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q),
      );
    }

    list = [...list].sort((a, b) => {
      if (sort === "newest") return 0; // already sorted by createdAt desc from Firestore
      if (sort === "oldest") return 1;
      if (sort === "name_asc") return a.name.localeCompare(b.name);
      if (sort === "name_desc") return b.name.localeCompare(a.name);
      return 0;
    });

    return list;
  }, [products, category, search, sort]);

  const pageTitle =
    category === "Photography"
      ? "Portfolio"
      : category === "AI Art"
        ? "AI Art"
        : "Collection";
  const pageEyebrow =
    category === "Photography"
      ? "Original Photography"
      : category === "AI Art"
        ? "AI Generated Art"
        : "Wall Art & Photography";
  const startingPrice = getStartingPrice(pricing);

  return (
    <>
      <GlobalStyle />
      <Page>
        {/* ── Hero ── */}
        <Hero>
          <HeroEyebrow>{pageEyebrow}</HeroEyebrow>
          <HeroTitle>
            {pageTitle === "Collection" ? (
              <>
                <em>The</em> {pageTitle}
              </>
            ) : (
              pageTitle
            )}
          </HeroTitle>
          <HeroMeta>
            <GoldLine />
            <HeroCount>
              {loading
                ? "Loading…"
                : `${filtered.length} ${filtered.length === 1 ? "piece" : "pieces"}`}
            </HeroCount>
          </HeroMeta>
        </Hero>

        {/* ── Toolbar ── */}
        <Toolbar>
          <SearchWrapper>
            <SearchIcon>⌕</SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search pieces…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrapper>

          {!category && (
            <FilterGroup>
              <FilterChip $active={false} onClick={() => {}}>
                All
              </FilterChip>
              <FilterChip
                $active={false}
                onClick={() => navigate("/portfolio")}
              >
                Photography
              </FilterChip>
              <FilterChip $active={false} onClick={() => navigate("/ai-art")}>
                AI Art
              </FilterChip>
            </FilterGroup>
          )}

          {!loading && (
            <ResultCount>
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </ResultCount>
          )}

          <SortSelect value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SortSelect>
        </Toolbar>

        {/* ── Grid ── */}
        <GridWrapper>
          <Grid>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i}>
                  <SkeletonImg />
                  <SkeletonInfo>
                    <SkeletonLine $w="70%" $h="14px" />
                    <SkeletonLine $w="40%" $h="10px" />
                  </SkeletonInfo>
                </SkeletonCard>
              ))
            ) : filtered.length === 0 ? (
              <EmptyWrap>
                <EmptyIcon>◻</EmptyIcon>
                <EmptyText>No pieces found</EmptyText>
                {search && (
                  <ClearBtn onClick={() => setSearch("")}>
                    Clear search
                  </ClearBtn>
                )}
              </EmptyWrap>
            ) : (
              filtered.map((product, i) => {
                const minPrice = startingPrice;
                return (
                  <Card
                    key={product.id}
                    $index={i}
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <CardImageWrap className="card-img">
                      {product.imageUrl ? (
                        <CardImg src={product.imageUrl} alt={product.name} />
                      ) : (
                        <NoImg>No Image</NoImg>
                      )}
                      <CardOverlay className="card-overlay">
                        <ViewBtn>View Piece</ViewBtn>
                      </CardOverlay>
                      <CardNumber>{String(i + 1).padStart(2, "0")}</CardNumber>
                    </CardImageWrap>

                    <CardInfo>
                      <CardTop>
                        <CardName>{product.name}</CardName>
                        <CardArrow className="card-arrow">→</CardArrow>
                      </CardTop>
                      <CardLine className="card-line" />
                      <CardBottom>
                        <CardCategory>{product.category}</CardCategory>
                        {minPrice && (
                          <CardPrice>
                            From <strong>€{minPrice.toFixed(2)}</strong>
                          </CardPrice>
                        )}
                      </CardBottom>
                    </CardInfo>
                  </Card>
                );
              })
            )}
          </Grid>
        </GridWrapper>
      </Page>
    </>
  );
};

export default Shop;
